import { ANSWERED_OUTCOMES, CALL_OUTCOMES } from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import type { Tone } from "../design/components/tone";
import type { Translate } from "../i18n/locale";

// Come l'interfaccia scrive i dati di un Lead che il server manda grezzi: il telefono, l'esito della chiamata.

/** I prefissi internazionali che il prodotto conosce (quelli del selettore di «Nuovo Lead»), dal più lungo. */
const COUNTRY_CODES = ["378", "39", "41", "33", "49", "43"] as const;

/**
 * Un numero in forma internazionale (`+393000001234`) diviso per leggerlo: il prefisso del paese, poi gruppi di tre
 * cifre e il resto in fondo (`+39 300 000 1234`). Un valore che non comincia con `+` resta com'è.
 */
export function formatPhoneForDisplay(e164: string): string {
  if (!e164.startsWith("+")) return e164;
  const digits = e164.slice(1);
  const code = COUNTRY_CODES.find((candidate) => digits.startsWith(candidate)) ?? digits.slice(0, 2);
  const national = digits.slice(code.length);
  const groups: string[] = [];
  let rest = national;
  // Gruppi di tre finché ne restano più di quattro: l'ultimo gruppo ha quattro cifre (o meno).
  while (rest.length > 4) {
    groups.push(rest.slice(0, 3));
    rest = rest.slice(3);
  }
  if (rest) groups.push(rest);
  return [`+${code}`, ...groups].join(" ");
}

/**
 * I caratteri di controllo che i testi del server rifiutano diventano spazi; tab e a capo restano (la ricerca li
 * tratta come spazi, una nota li tiene). Anche la metà sola di un'emoji, che il server rifiuta allo stesso modo
 * (decisione del 1° ottobre 2026), diventa uno spazio. Per la ricerca dell'elenco, quella della Timeline e le note.
 */
export function withoutControlCharacters(value: string): string {
  // Le stesse classi dei contratti (`multiLineTextSchema`): ogni carattere di controllo Unicode tranne tab e a capo, e
  // con il flag `u` le sole metà di una coppia di surrogati senza l'altra (`\p{Cs}`).
  return value.replace(/[^\P{Cc}\t\n\r]|\p{Cs}/gu, " ");
}

/** Basta a riconoscere uno sbaglio di battitura in un'email; l'email vera la controlla il server. */
export const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** I prefissi del telefono, come nel mock-up: +39 di partenza («Nuovo Lead» e la scheda). */
export const PHONE_PREFIXES = [
  { value: "+39", label: "newLead.prefixes.it" },
  { value: "+41", label: "newLead.prefixes.ch" },
  { value: "+378", label: "newLead.prefixes.sm" },
  { value: "+33", label: "newLead.prefixes.fr" },
  { value: "+49", label: "newLead.prefixes.de" },
  { value: "+43", label: "newLead.prefixes.at" },
] as const satisfies ReadonlyArray<{ value: string; label: MessageKey }>;

/**
 * I prefissi dopo i quali lo zero iniziale del numero nazionale non si scrive: è il prefisso interurbano («079 …» in
 * Svizzera è «+41 79 …»). In Italia e a San Marino lo zero del fisso fa parte del numero anche dopo il prefisso.
 */
const TRUNK_ZERO_PREFIXES: ReadonlySet<string> = new Set(["+41", "+33", "+49", "+43"]);

/**
 * Il telefono come lo riceve il server: il prefisso scelto davanti al numero, a meno che il numero non ne porti già
 * uno (`+41 …`, `0041 …`), senza lo zero interurbano dove non si scrive. Vuoto se il numero è vuoto.
 */
export function composePhone(prefix: string, raw: string): string {
  const value = raw.trim();
  if (!value) return "";
  if (value.startsWith("+") || value.startsWith("00")) return value;
  const national = TRUNK_ZERO_PREFIXES.has(prefix) ? value.replace(/^0\s*/, "") : value;
  return `${prefix} ${national}`;
}

/**
 * Un numero in forma internazionale diviso per la casella con il prefisso separato: il prefisso, se è fra quelli del
 * selettore, e il resto delle cifre. Un prefisso che il selettore non conosce resta nel numero, con +39 nel selettore.
 */
export function splitPhone(e164: string | null): { prefix: string; national: string } {
  if (!e164) return { prefix: PHONE_PREFIXES[0].value, national: "" };
  const known = PHONE_PREFIXES.find((option) => e164.startsWith(option.value));
  return known ? { prefix: known.value, national: e164.slice(known.value.length) } : { prefix: PHONE_PREFIXES[0].value, national: e164 };
}

/**
 * Il numero scritto nella forma internazionale, soltanto per mostrarlo subito prima della risposta del server (che lo
 * normalizza davvero): cifre e `+`, `00` iniziale come `+`.
 */
export function roughE164(composed: string): string {
  const compact = composed.replace(/[^\d+]/g, "");
  return compact.startsWith("00") ? `+${compact.slice(2)}` : compact;
}

/** Il nome con cui un Lead si legge: nome e cognome, oppure, senza, il suo telefono (come nel mock-up). */
export function leadName(lead: { displayName: string | null; primaryPhone: string | null }): string | null {
  const name = lead.displayName?.trim();
  if (name) return name;
  return lead.primaryPhone ? formatPhoneForDisplay(lead.primaryPhone) : null;
}

/**
 * Quello che mostra la prima colonna dell'elenco, «Cognome» (decisione del 6 ottobre 2026): il cognome, oppure, senza, lo
 * stesso ripiego della colonna «Lead» di prima (`leadName`: il nome, il salone o il telefono), così ogni riga ha un link
 * che si legge.
 */
export function leadSurname(lead: { lastName: string | null; displayName: string | null; primaryPhone: string | null }): string | null {
  return lead.lastName?.trim() || leadName(lead);
}

// Il primo passo dell'esito e, dopo «Risposta», il secondo (piano M4, Task 10): la colonna e il filtro «Esito dell'ultima
// chiamata» dicono il passo più preciso. Gli esiti sono quelli dei contratti, e ognuno ha la sua chiave nei cataloghi: un
// esito nuovo senza la sua etichetta non compila.
const OUTCOME_KEYS: ReadonlyMap<string, MessageKey> = new Map<string, MessageKey>([
  ...CALL_OUTCOMES.map((outcome) => [outcome, `outcomes.${outcome}`] as const),
  ...ANSWERED_OUTCOMES.map((outcome) => [outcome, `answeredOutcomes.${outcome}`] as const),
]);

/** Gli esiti che l'interfaccia sa dire: il primo passo, poi il secondo. */
export const KNOWN_OUTCOMES: readonly string[] = [...OUTCOME_KEYS.keys()];

/** L'esito normalizzato di una chiamata con le parole della postazione; un esito che il catalogo non conosce resta com'è. */
export function outcomeLabel(t: Translate, outcome: string): string {
  const key = OUTCOME_KEYS.get(outcome);
  return key ? t(key) : outcome;
}

/**
 * Il tono della pillola dello status, come nel mock-up (`STATUS_TONE`), dal nome degli status del catalogo di partenza:
 * il colore si aggiunge al testo, che resta sempre leggibile da solo. Uno status rinominato o nuovo resta neutro.
 */
const STATUS_TONES: Readonly<Record<string, Tone>> = {
  "In target": "accent",
  Qualificato: "success",
  Cliente: "success",
  "Numero Errato": "danger",
  "Black List": "danger",
  "Non vuole essere chiamato": "danger",
  "Fuori Settore": "warning",
  "Competitor o Venditore o Altro": "warning",
  Barbiere: "warning",
  "Dipendente che vuole aprire Salone": "warning",
  "Dipendente Non in Linea": "warning",
};

export function statusTone(name: string): Tone {
  return STATUS_TONES[name] ?? "neutral";
}
